/**
 * Session 60.17 · Métier Hours · Post-Redemption Social Share
 *
 * Client polling hook + UI helpers for the "share your redemption" prompt.
 * The scan happens on the partner's device (their phone opens the verify
 * URL), so we surface the prompt on the member's device via a light poll
 * while the QR view is fresh in localStorage. No push required.
 *
 * Design rules from Cam:
 *   · Never block redemption
 *   · Never leak PII in the shared URL / metadata
 *   · Only count a share as "started" (native sheet opened), never "completed"
 *   · 30-day per-partner suppression after dismissal
 */
import { useEffect, useRef, useState, useCallback } from "react";
import api from "./api";

const LS_QR_VIEWED_AT = "metier_hours_qr_viewed_at";
const QR_VIEW_WINDOW_MS = 30 * 60 * 1000; // poll for 30 min after member opened QR
const POLL_INTERVAL_MS = 8000;

/** Call when the member views their Métier Hours QR so polling turns on. */
export function markHoursQrViewed() {
  try { localStorage.setItem(LS_QR_VIEWED_AT, String(Date.now())); }
  catch { /* private-mode safari, ignore */ }
}

/** True if the member opened their QR within the poll window. */
function qrRecentlyViewed() {
  try {
    const t = Number(localStorage.getItem(LS_QR_VIEWED_AT) || 0);
    return t > 0 && Date.now() - t < QR_VIEW_WINDOW_MS;
  } catch { return false; }
}

/**
 * Build the share payload. Contains ONLY partner_slug + share_id + UTM.
 * No user_id / scan_id / email / QR token.
 */
export function buildShare(scan) {
  const partnerName = scan.partner_name || "a Métier Hours partner";
  const perk = (scan.perk_label || "member perk").trim();
  const share_id = `sh_${Math.random().toString(36).slice(2, 10)}`;
  const params = new URLSearchParams({
    utm_source: "member_share",
    utm_medium: "social",
    utm_campaign: "hours_share",
    partner: scan.partner_slug || "",
    share_id,
  });
  const url = `https://workmetier.app/hours?${params.toString()}`;
  const text = [
    `Working local today at ${partnerName} \u2615\uFE0F`,
    `Just unlocked my ${perk} with Métier.`,
    `Find work-friendly local spots and member perks on Métier.`,
  ].join("\n");
  return { title: `Métier Hours · ${partnerName}`, text, url, share_id };
}

/**
 * usePendingHoursShare — polling hook. Returns the pending scan (or null),
 * plus helpers to share/dismiss/report analytics. Runs only:
 *   · when authenticated
 *   · when the tab is visible
 *   · when the member opened their QR within QR_VIEW_WINDOW_MS
 */
export function usePendingHoursShare(user) {
  const [scan, setScan] = useState(null);
  const timer = useRef(null);

  const post = useCallback(async (scanId, event, extra = {}) => {
    try { await api.post("/hours/share-events", { scan_id: scanId, event, ...extra }); }
    catch { /* analytics best-effort */ }
  }, []);

  const dismiss = useCallback(async () => {
    if (!scan) return;
    await post(scan.scan_id, "dismissed");
    setScan(null);
  }, [scan, post]);

  const share = useCallback(async () => {
    if (!scan) return;
    const payload = buildShare(scan);
    // Prefer native / Web Share API; fall back to clipboard.
    let method = null;
    try {
      if (navigator.share) {
        await navigator.share({ title: payload.title, text: payload.text, url: payload.url });
        method = "web_share_api";
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(`${payload.text}\n${payload.url}`);
        method = "copy_link";
        await post(scan.scan_id, "link_copied", { method, share_id: payload.share_id });
      }
    } catch (e) {
      // Web Share can throw when the user cancels — that's still "started".
      // If clipboard threw, method stays null and we still record "started"
      // so the funnel shows the attempt.
    }
    await post(scan.scan_id, "started", { method: method || "unknown", share_id: payload.share_id });
    // Close the prompt after share attempt — do NOT set suppression on
    // successful share; only dismissal triggers the 30-day cooldown.
    setScan(null);
  }, [scan, post]);

  useEffect(() => {
    if (!user?.user_id) return undefined;

    const poll = async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      if (!qrRecentlyViewed()) return;
      try {
        const r = await api.get("/hours/my-latest-scan");
        const next = r.data?.scan;
        if (next && (!scan || next.scan_id !== scan.scan_id)) {
          setScan(next);
          post(next.scan_id, "shown");
        }
      } catch { /* offline / 401, ignore */ }
    };

    // fire once immediately if window is fresh, then interval
    poll();
    timer.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [user?.user_id, scan, post]);

  return { scan, share, dismiss };
}
